import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";
import { useSearchParams } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";

import Box from "@mui/material/Box";
import MaterialModal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";

import { Toast } from "@covertree/utils";
import { theme } from "@covertree/theme";
import { Button, IconButton } from "@covertree/ui";

import {
  checkEndorsementEligibility,
  discardEndorsements,
  loadEndorsement,
  selectEndorsementEffectiveDateBounds,
  selectEndorsementId,
  selectEndorsementLoading,
  selectEndorsementPopulated,
  selectHasUnsavedChanges,
  selectIsRecalculating,
  selectNeedsUnderwritingReview,
  selectIsPriceUnavailable,
  selectIsUnderwritingFailed,
  selectIsViewOnly,
  selectMode,
  selectPremiumDelta,
  setContext,
  setEndorsementError,
  useEndorsementSelector,
} from "@redux/slices/endorsement";
import { useAppDispatch } from "@redux/hooks";
import {
  EndorsementRoutes,
  navigateToEndorsementStep,
  POLICIES_PATH,
} from "@routing/paths";
import { useResetEndorsement } from "@hooks/endorsement";
import { usePolicyDetails } from "@routing/routes/Policies/PolicyDetails/PolicyDetailsContext";
import type { PolicyDetailsContentProps } from "@routing/routes/Policies/PolicyDetails/PolicyDetailsContent/types";

import { DiscardEndorsementModal } from "@components/EndorsementsModals/DiscardEndorsementModal";
import { EndorsementForm } from "./EndorsementForm";
import { EndorsementLayoutFooter } from "./components/EndorsementLayoutFooter";
import { EndorsementLayoutHeader } from "./components/EndorsementLayoutHeader";
import { EndorsementLayoutStepper } from "./components/EndorsementLayoutStepper";
import {
  getLockedSteps,
  getStepOrder,
  ISSUE_BUTTON_LABEL,
  LOADED_ON_OPEN_MODES,
  MODE_EYEBROW,
  NEXT_BUTTON_LABEL,
  PRICE_HIDDEN_STEPS,
  STEP_ORDER,
} from "./constants";
import { EndorsementFormHandle, EndorsementOutletContext } from "./types";
import { useEndorsementIssue } from "./hooks/useEndorsementIssue";
import { useRedirectFailedEndorsement } from "./hooks/useRedirectFailedEndorsement";
import { useLockUnderwritingFailed } from "./hooks/useLockUnderwritingFailed";

export const useEndorsementOutletContext = () =>
  useOutletContext<EndorsementOutletContext>();

export function EndorsementLayout() {
  const { policyLocator = "" } = useParams();
  const [searchParams] = useSearchParams();
  const endorsementId = searchParams.get("endorsementId");

  const policyDetails = usePolicyDetails();
  const policy = policyDetails?.policy as
    | PolicyDetailsContentProps["policy"]
    | undefined;
  const policyLoading = !!policyDetails?.loading;
  const policyError = !!policyDetails?.error;

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const resetEndorsement = useResetEndorsement();
  const formRef = useRef<EndorsementFormHandle>(null);

  const mode = useEndorsementSelector(selectMode);
  const isViewOnly = useEndorsementSelector(selectIsViewOnly);
  const premiumDelta = useEndorsementSelector(selectPremiumDelta);
  const isPriceUnavailable = useEndorsementSelector(selectIsPriceUnavailable);
  const isRecalculating = useEndorsementSelector(selectIsRecalculating);
  const hasUnsavedChanges = useEndorsementSelector(selectHasUnsavedChanges);
  const isUnderwritingFailed = useEndorsementSelector(
    selectIsUnderwritingFailed
  );
  const needsUnderwritingReview = useEndorsementSelector(
    selectNeedsUnderwritingReview
  );
  const endorsementIdState = useEndorsementSelector(selectEndorsementId);
  const loading = useEndorsementSelector(selectEndorsementLoading);
  const endorsementPopulated = useEndorsementSelector(
    selectEndorsementPopulated
  );
  const { minDate, maxDate } = useEndorsementSelector(
    selectEndorsementEffectiveDateBounds
  );

  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  useRedirectFailedEndorsement(
    mode,
    policyLocator,
    endorsementId ?? endorsementIdState,
    policy
  );

  const currentStep = useMemo<EndorsementRoutes>(() => {
    const segment = pathname.split("/").pop();
    return (
      STEP_ORDER.find((step) => step === segment) ??
      EndorsementRoutes.INSURED_INFO
    );
  }, [pathname]);

  useLockUnderwritingFailed(
    isUnderwritingFailed,
    currentStep,
    policyLocator,
    endorsementId ?? endorsementIdState
  );

  const stepOrder = getStepOrder(needsUnderwritingReview);
  const currentIndex = stepOrder.indexOf(currentStep);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === stepOrder.length - 1;
  const nextStep = stepOrder[currentIndex + 1];
  // Without the eligibility window the effective-date field is disabled (see
  // `PolicyTerm`) and the schema drops its min/max checks, so a stale date would
  // submit unvalidated. View-only modes never post a date, so they are exempt
  // rather than trapped on the step.
  const isEffectiveDateWindowMissing =
    currentStep === EndorsementRoutes.INSURED_INFO &&
    !isViewOnly &&
    (!minDate || !maxDate);
  // create/update have Save/Discard menu and Check price; issue/review have neither
  const isEditMode = mode === "create" || mode === "update";

  const checkEligibility = useCallback(
    async (policyLocator: string) => {
      try {
        const result = await dispatch(
          checkEndorsementEligibility({
            policyLocator,
          })
        ).unwrap();

        if (!result.isEligible) {
          dispatch(
            setEndorsementError({
              cause: "Policy not eligible for endorsement",
              message: result.reason ?? "Unknown",
              name: "Endorsement eligibility",
            })
          );
        }
      } catch (e) {
        datadogRum.addError("Failed to check endorsement eligibility", { e });
        Toast.showError(
          "Failed to check endorsement eligibility. Please try again later."
        );
      }
    },
    [policyLocator]
  );

  const initializePolicy = useCallback(() => {
    if (isViewOnly && endorsementPopulated) return;

    dispatch(setContext({ policyLocator, mode, endorsementId }));
    if (LOADED_ON_OPEN_MODES.includes(mode)) {
      dispatch(loadEndorsement({ policyLocator, mode, endorsementId }));
    }
  }, [policyLocator, mode, endorsementId, isViewOnly, endorsementPopulated]);

  // Initialise policy/mode context from the route. `issue`/`review` land past
  // the first step, so load the endorsement up front (create/update load it on
  // the first-step submit instead).
  useEffect(() => {
    initializePolicy();

    if (!minDate || !maxDate) {
      checkEligibility(policyLocator);
    }
  }, [dispatch, policyLocator, mode, endorsementId, minDate, maxDate]);

  // Reset slice state when leaving the flow (global store no longer auto-resets).
  const resetEndorsementRef = useRef(resetEndorsement);
  resetEndorsementRef.current = resetEndorsement;

  useEffect(() => {
    return () => {
      resetEndorsementRef.current();
    };
  }, []);

  const close = useCallback(
    (path?: string) => {
      navigate(path ?? `/${POLICIES_PATH}/${policyLocator}`);
    },
    [navigate, policyLocator]
  );

  const { handleSubmitIssue, disableSubmit, isIssuing } = useEndorsementIssue({
    onSuccess: close,
  });

  const handleCloseRequest = useCallback(() => {
    if (isIssuing) {
      return;
    }

    if (isEditMode && hasUnsavedChanges) {
      setShowUnsavedPrompt(true);
      return;
    }
    close();
  }, [isIssuing, isEditMode, hasUnsavedChanges, close]);

  const recalculate = useCallback(() => {
    formRef.current?.handleReprice();
  }, []);

  const goBack = useCallback(async () => {
    if (isRecalculating || isFirstStep) return;
    await navigateToEndorsementStep(
      navigate,
      policyLocator,
      stepOrder[currentIndex - 1],
      { endorsementId }
    );
  }, [
    isRecalculating,
    isFirstStep,
    navigate,
    policyLocator,
    stepOrder,
    currentIndex,
    endorsementId,
  ]);

  const handleDiscard = useCallback(() => {
    setShowDiscardModal(true);
  }, []);

  const handleDiscardConfirm = useCallback(async () => {
    if (!endorsementIdState) {
      // No endorsement created yet, just reset and close
      resetEndorsement();
      close();
      return;
    }
    const result = await dispatch(
      discardEndorsements({
        policyLocator: policy.policyLocator,
        endorsementLocators: [endorsementIdState],
      })
    );
    if (discardEndorsements.fulfilled.match(result)) {
      setShowDiscardModal(false);
      close();
    }
  }, [dispatch, endorsementIdState, resetEndorsement, close]);

  const handleDiscardModalClose = useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const handleLeaveWithoutSaving = useCallback(() => {
    setShowUnsavedPrompt(false);
    resetEndorsement();
    close();
  }, [resetEndorsement, close]);

  const handleSaveAsDraftAndClose = useCallback(() => {
    setShowUnsavedPrompt(false);
    formRef.current?.handleSaveAsDraftAndClose();
  }, []);

  const eyebrow = MODE_EYEBROW[mode];
  const headerTitle =
    mode === "create" && !endorsementIdState
      ? `Policy #${policyLocator}`
      : `Endorsement #${endorsementIdState} · Policy #${policyLocator}`;

  return (
    <>
      <MaterialModal
        open
        onClose={handleCloseRequest}
        aria-label="Endorsement modal"
        sx={{
          zIndex: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}>
        <Paper
          sx={{
            width: "100%",
            maxWidth: 900,
            height: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            outline: "none",
            position: "relative",
          }}>
          <EndorsementLayoutHeader
            eyebrow={eyebrow}
            title={headerTitle}
            isEditMode={isEditMode && !isUnderwritingFailed}
            isIssueMode={isViewOnly && !isUnderwritingFailed}
            disabled={isIssuing}
            onSave={() => formRef.current?.handleSave()}
            onDiscard={handleDiscard}
            onClose={handleCloseRequest}
          />

          <EndorsementLayoutStepper
            steps={stepOrder}
            currentStep={currentStep}
            lockedSteps={getLockedSteps(isViewOnly, isUnderwritingFailed)}
          />

          {/* Content */}
          <Box py={3} px={3} flex={1} overflow="auto">
            <EndorsementForm
              ref={formRef}
              policy={policy}
              policyLoading={policyLoading}
              policyError={policyError}
              currentStep={currentStep}
              nextStep={nextStep}
              isLastStep={isLastStep}
              isEditMode={isEditMode}
              onClose={close}
              onSaveDraftError={() => setShowUnsavedPrompt(true)}
            />
          </Box>

          <EndorsementLayoutFooter
            hidePrice={PRICE_HIDDEN_STEPS.includes(currentStep)}
            premiumDelta={premiumDelta}
            isPriceUnavailable={isPriceUnavailable}
            isRecalculating={isRecalculating}
            isEditMode={isEditMode}
            canRecalculate={hasUnsavedChanges}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            nextLabel={
              nextStep ? NEXT_BUTTON_LABEL[nextStep] : ISSUE_BUTTON_LABEL
            }
            disableNext={
              (isLastStep && disableSubmit) ||
              isEffectiveDateWindowMissing ||
              (currentStep === EndorsementRoutes.UW_REVIEW &&
                isUnderwritingFailed)
            }
            isSubmitting={isIssuing}
            onRecalculate={recalculate}
            onBack={goBack}
            onNext={
              isLastStep
                ? handleSubmitIssue
                : () => formRef.current?.handleNext()
            }
          />

          {loading.show && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}>
              <CircularProgress />
            </Box>
          )}
        </Paper>
      </MaterialModal>

      {/* Unsaved changes prompt */}
      <MaterialModal
        open={showUnsavedPrompt}
        onClose={() => setShowUnsavedPrompt(false)}
        aria-label="Unsaved changes"
        sx={{
          zIndex: 1300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}>
        <Paper sx={{ p: 3, maxWidth: 440, width: "100%", outline: "none" }}>
          <Box display="flex" mb={4}>
            <Typography sx={{ flexGrow: 1 }} variant="h3">
              Unsaved changes
            </Typography>

            <IconButton
              onClick={() => setShowUnsavedPrompt(false)}
              aria-label="Close modal">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography color={theme.palette.grey[800]} mb={5}>
            You have unsaved changes. Would you like to save this endorsement as
            a draft before leaving?
          </Typography>
          <Stack direction="row" gap={2}>
            <Button onClick={handleSaveAsDraftAndClose}>Save</Button>
            <Button variant="outlined" onClick={handleLeaveWithoutSaving}>
              Leave without saving
            </Button>
          </Stack>
        </Paper>
      </MaterialModal>

      {/* Discard endorsement confirmation */}
      <DiscardEndorsementModal
        isModalOpen={showDiscardModal}
        onClose={handleDiscardModalClose}
        onConfirm={handleDiscardConfirm}
      />
    </>
  );
}
